import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { useState } from 'react'
import * as z from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useBfnTokenAbrufMutation } from '@/features/befehlsnachrichten/mutations/useBfnTokenAbrufMutation'
import { ChevronRightIcon32, ExpandMoreIcon32 } from '@repo/ui/icons'
import { useLoadingWithDelay } from '@/hooks/useLoadingWithDelay'
import { useNavigate } from 'react-router-dom'
import { AxiosError, HttpStatusCode } from 'axios'
import { useBefehlsnachrichtUpdater } from '@/features/befehlsnachrichten/hooks/useBefehlsnachrichtUpdater'
import { useBfnStore } from '@/store/useBfnStore'
import { useZugnummerStore } from '@/store/useZugnummerStore'
import { Abruftoken } from '@/models/Abruftoken'
import { useShallow } from 'zustand/react/shallow'
import useOnlineDetection from '@/features/offline/hooks/useOnlineDetection'
import {
  allowedChars,
  Button,
  cn,
  ErrorAlert,
  Heading,
  InfoAlert,
  Input,
  isNetworkError,
  LoadingIndicator,
  normalizeHttpError,
  SimpleApplyCancelDialog,
  UndoIcon24,
  useDialog,
  ZUGNUMMER_MAX_LENGTH,
  ZUGNUMMER_MIN_LENGTH,
  ZUGNUMMER_REGEXP,
  ZUGRIFFSCODE_MAX_LENGTH,
  ZUGRIFFSCODE_MIN_LENGTH,
  ZUGRIFFSCODE_NOT_EMPTY_MIN_LENGTH,
  ZUGRIFFSCODE_REGEXP
} from '@repo/ui'

export interface BfnAbrufenProps {
  'data-testid': string
}

export default function BfnAbrufen({
  'data-testid': testId
}: Readonly<BfnAbrufenProps>) {
  const { t } = useTranslation('bfn')
  const navigate = useNavigate()
  const isOnline = useOnlineDetection()
  const addPendingToken = useBfnStore(
    useShallow((state) => state.addPendingToken)
  )
  const {
    zugnummer: savedZugnummer,
    setZugnummer,
    clearZugnummer
  } = useZugnummerStore(
    useShallow((state) => ({
      zugnummer: state.zugnummer,
      setZugnummer: state.setZugnummer,
      clearZugnummer: state.clearZugnummer
    }))
  )
  const updateBefehlsnachricht = useBefehlsnachrichtUpdater()
  const [isResetDialogOpen, openResetDialog, closeResetDialog] = useDialog()

  const isZugnummerEingeloggt = savedZugnummer !== ''

  const formDataSchema = z.object({
    zugnummer: z
      .string()
      .min(ZUGNUMMER_MIN_LENGTH, {
        message: t('abrufen.errors.zugnummer.required')
      })
      .max(ZUGNUMMER_MAX_LENGTH, {
        message: t('abrufen.errors.zugnummer.maxlength', {
          maxlength: ZUGNUMMER_MAX_LENGTH
        })
      })
      .regex(ZUGNUMMER_REGEXP, {
        message: t('abrufen.errors.zugnummer.required')
      }),
    zugriffscode: z
      .string()
      .min(ZUGRIFFSCODE_NOT_EMPTY_MIN_LENGTH, {
        message: t('abrufen.errors.zugriffscode.required')
      })
      .min(ZUGRIFFSCODE_MIN_LENGTH, {
        message: t('abrufen.errors.zugriffscode.minlength')
      })
      .max(ZUGRIFFSCODE_MAX_LENGTH, {
        message: t('abrufen.errors.zugriffscode.maxlength', {
          maxlength: ZUGRIFFSCODE_MAX_LENGTH
        })
      })
      .superRefine(allowedChars(ZUGRIFFSCODE_REGEXP))
  })

  type FormData = z.infer<typeof formDataSchema>

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
    watch,
    reset,
    trigger
  } = useForm<FormData>({
    resolver: zodResolver(formDataSchema),
    defaultValues: { zugnummer: savedZugnummer, zugriffscode: '' }
  })

  const currentZugnummer = watch('zugnummer')
  const hasZugnummerChanged =
    isZugnummerEingeloggt && currentZugnummer !== savedZugnummer

  const isZugnummerEinloggenButtonEnabled =
    !isZugnummerEingeloggt || (isZugnummerEingeloggt && hasZugnummerChanged)
  const isBefehlsnachrichtAbrufenButtonEnabled =
    !isZugnummerEingeloggt || (isZugnummerEingeloggt && !hasZugnummerChanged)

  const [isExpanded, setIsExpanded] = useState<boolean>(true)

  const { mutation } = useBfnTokenAbrufMutation()
  const showLoading = useLoadingWithDelay(mutation.isPending)
  const [info, setInfo] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const onSubmit = async (formData: FormData) => {
    setError(null)

    const onSuccess = async (abruftoken: Abruftoken) => {
      setZugnummer(formData.zugnummer)
      try {
        const befehlsnachricht = await updateBefehlsnachricht(abruftoken)
        navigate(`/befehlsnachrichten/${befehlsnachricht.id}`)
      } catch {
        // Graceful error handling: the abruftoken is already saved, so we'll inform the user about the situation. The Befehlsnachricht will be automatically updated in the background.
        addPendingToken(abruftoken)
        setInfo(t('abrufen.info.continuedInBackground'))
      }
    }

    const onError = (err: unknown) => {
      // Backend Error
      if (err instanceof AxiosError) {
        // Response 404 Not Found
        if (err.response?.status === HttpStatusCode.NotFound) {
          setError(t('abrufen.errors.response.bfn_nicht_gefunden'))
          setErrorMessage(null)
        }

        // Response 409 Conflict
        else if (err.response?.status === HttpStatusCode.Conflict) {
          if (err.response.data.errorCode === 'ERSTABRUF_NICHT_ABGESCHLOSSEN') {
            setError(t('abrufen.errors.response.erstabruf_nicht_abgeschlossen'))
            setErrorMessage(null)
          } else if (err.response.data.errorCode === 'BFN_ABGEWIESEN') {
            setError(t('abrufen.errors.response.bfn_abgewiesen'))
            setErrorMessage(null)
          }
        }

        // Response 410 Gone
        else if (err.response?.status === HttpStatusCode.Gone) {
          setError(t('abrufen.errors.response.bfn_geloescht'))
          setErrorMessage(null)
        }

        // Response 429  Too Many Requests
        else if (err.response?.status === HttpStatusCode.TooManyRequests) {
          setError(t('abrufen.errors.response.zu_viele_eingaben'))
          setErrorMessage(null)
        }

        // Response timeout or network error
        else if (
          err.response?.status === HttpStatusCode.RequestTimeout ||
          isNetworkError(err)
        ) {
          setError(t('abrufen.errors.response.unexpected'))
          setErrorMessage(normalizeHttpError(err))
        }

        // Response unexpected
        else {
          setError(t('abrufen.errors.response.unexpected'))
          setErrorMessage(null)
        }
      }

      // Other errors
      else if (err instanceof Error) {
        setError(err.message)
        setErrorMessage(null)
      } else {
        setError(String(err))
        setErrorMessage(null)
      }
    }

    mutation.mutate(
      {
        zugnummer: Number(formData.zugnummer),
        zugriffscode: formData.zugriffscode
      },
      {
        onSuccess: (abruftoken) => {
          onSuccess(abruftoken)
        },
        onError: (err) => {
          onError(err)
        }
      }
    )
  }

  const handleZugnummerLock = async () => {
    const validZugnummer = await trigger('zugnummer')
    if (validZugnummer) {
      const normalizedZugnummer = getValues('zugnummer').replace(/^0+/, '')
      setValue('zugnummer', normalizedZugnummer)
      setZugnummer(normalizedZugnummer)
    }
  }

  const onApplyReset = () => {
    clearZugnummer()

    reset({
      zugnummer: '',
      zugriffscode: ''
    })

    closeResetDialog()
  }

  return (
    <>
      <SimpleApplyCancelDialog
        title={t('abrufen.reset.title')}
        state={isResetDialogOpen}
        onApply={onApplyReset}
        onCancel={closeResetDialog}
        applyButtonText={t('abrufen.reset.apply')}
        cancelButtonText={t('abrufen.reset.cancle')}
        data-testid={`${testId}-reset-dialog`}
      >
        <p data-testid={`${testId}-reset-text`}>{t('abrufen.reset.text')}</p>
      </SimpleApplyCancelDialog>

      <div className="flex flex-col border rounded-lg p-1 dark:bg-cool-gray-800">
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex-1 flex gap-0.5 items-center"
          data-testid={`${testId}-expand-button`}
        >
          {isExpanded ? (
            <ExpandMoreIcon32
              data-testid={`${testId}-expand-more-button-icon`}
            />
          ) : (
            <ChevronRightIcon32
              data-testid={`${testId}-chevron-right-button-icon`}
            />
          )}
          <Heading tag="h2">{t('abrufen.title')}</Heading>
        </button>

        {isExpanded && (
          <form
            className="flex flex-col gap-1 pt-1"
            onSubmit={handleSubmit(onSubmit)}
          >
            {info && (
              <InfoAlert data-testid={`${testId}-info`}>{info}</InfoAlert>
            )}
            {error && (
              <ErrorAlert error={errorMessage} data-testid={`${testId}-error`}>
                {error}
              </ErrorAlert>
            )}
            <div className="grid grid-cols-1 gap-1 lg:grid-cols-2">
              <div className="flex flex-col gap-1 items-end">
                <Input
                  className="flex-1 w-full"
                  type="text"
                  label={t('abrufen.zugnummer')}
                  variant="solid"
                  errors={errors}
                  maxLength={ZUGNUMMER_MAX_LENGTH}
                  {...register('zugnummer')}
                  data-testid={`${testId}-zugnummer`}
                />
                <div
                  className={cn(
                    'flex gap-1 w-full sm:flex-row flex-col',
                    !isZugnummerEingeloggt && 'md:w-fit md:justify-end',
                    isZugnummerEingeloggt && 'md:justify-between '
                  )}
                >
                  {isZugnummerEingeloggt && (
                    <Button
                      variant="secondary-outline"
                      className="flex-1 md:flex-none text-nowrap"
                      onClick={() => openResetDialog()}
                      disabled={!isZugnummerEingeloggt}
                      data-testid={`${testId}-zugnummer-reset-button`}
                    >
                      <UndoIcon24
                        data-testid={`${testId}-zugnummer-reset-icon`}
                      ></UndoIcon24>
                      <span
                        className="pl-0.5"
                        data-testid={`${testId}-zugnummer-reset-text`}
                      >
                        {t('abrufen.reset.button')}
                      </span>
                    </Button>
                  )}
                  <Button
                    variant="secondary-solid"
                    className="flex-1 md:flex-none text-nowrap"
                    onClick={handleZugnummerLock}
                    disabled={!isZugnummerEinloggenButtonEnabled}
                    data-testid={`${testId}-zugnummer-lock-button`}
                  >
                    {t('abrufen.zugnummerlock')}
                  </Button>
                </div>
              </div>
              <div className="flex flex-col gap-1 md:items-end">
                <Input
                  className="flex-1 w-full"
                  type="text"
                  label={t('abrufen.zugriffscode')}
                  variant="solid"
                  maxLength={ZUGRIFFSCODE_MAX_LENGTH}
                  errors={errors}
                  {...register('zugriffscode')}
                  data-testid={`${testId}-zugriffscode`}
                />
                {showLoading ? (
                  <div className="flex items-center space-x-0.5 h-2.75">
                    <LoadingIndicator
                      size="xs"
                      data-testid={`${testId}-loading`}
                    />
                    <span>{t('abrufen.loading')}</span>
                  </div>
                ) : (
                  <Button
                    type="submit"
                    className="w-full md:w-fit"
                    disabled={
                      mutation.isPending ||
                      !isOnline ||
                      !isBefehlsnachrichtAbrufenButtonEnabled
                    }
                    data-testid={`${testId}-submit-button`}
                  >
                    {t('abrufen.submit')}
                  </Button>
                )}
              </div>
            </div>
          </form>
        )}
      </div>
    </>
  )
}
